import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Layers, UserCog, LogOut, Menu, ShieldCheck, Eye, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageIcon } from "@/lib/icons";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
    isActive ? "bg-blue-600/15 text-blue-300 border-l-2 border-blue-500" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
  }`;

function Brand() {
  return (
    <div className="px-3">
      <div className="inline-block rounded-md bg-white px-3 py-2">
        <img src="/brand/logo-marigot.png" alt="Marigot Informatica" className="h-9 w-auto" data-testid="sidebar-logo" />
      </div>
    </div>
  );
}

function NavItems({ onNavigate }) {
  const { effectiveRole } = useAuth();
  const [pages, setPages] = useState([]);
  useEffect(() => {
    if (effectiveRole === "client") api.get("/pages").then((r) => setPages(r.data)).catch(() => {});
  }, [effectiveRole]);

  if (effectiveRole === "admin") {
    return (
      <nav className="space-y-1" onClick={onNavigate}>
        <NavLink end to="/admin" className={linkCls} data-testid="admin-nav-dashboard"><LayoutDashboard size={17} />Dashboard</NavLink>
        <NavLink to="/admin/clienti" className={linkCls} data-testid="admin-nav-clients"><Users size={17} />Clienti</NavLink>
        <NavLink to="/admin/pagine" className={linkCls} data-testid="admin-nav-pages"><Layers size={17} />Pagine</NavLink>
        <NavLink to="/profilo" className={linkCls} data-testid="nav-profile"><UserCog size={17} />Il mio profilo</NavLink>
      </nav>
    );
  }
  return (
    <nav className="space-y-1" onClick={onNavigate}>
      <NavLink end to="/portale" className={linkCls} data-testid="sidebar-nav-dashboard"><LayoutDashboard size={17} />Panoramica</NavLink>
      <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-slate-600">Servizi e documenti</div>
      {pages.map((p) => (
        <NavLink key={p.id} to={`/portale/pagina/${p.id}`} className={linkCls} data-testid={`sidebar-nav-page-${p.slug}`}>
          <PageIcon name={p.icon} size={17} />
          <span className="truncate">{p.title}</span>
        </NavLink>
      ))}
      <div className="pt-4" />
      <NavLink to="/profilo" className={linkCls} data-testid="sidebar-nav-page-profile"><UserCog size={17} />Profilo</NavLink>
    </nav>
  );
}

function Sidebar({ onNavigate }) {
  return (
    <div className="flex h-full flex-col gap-8 py-6 px-3">
      <Brand />
      <div className="flex-1 overflow-y-auto"><NavItems onNavigate={onNavigate} /></div>
      <div className="px-3 text-[11px] text-slate-600">© Marigot Informatica</div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, isViewingAs, viewAs, companyName } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };
  const exitViewAs = () => navigate(`/admin/clienti/${viewAs.id}`);
  const subtitle = isViewingAs ? `Vista cliente · ${companyName}` : user.role === "admin" ? "Amministratore" : companyName;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <aside className={`hidden lg:block fixed left-0 w-64 bg-[#0F172A] border-r border-slate-800 ${isViewingAs ? "top-10 bottom-0" : "inset-y-0"}`}>
        <Sidebar />
      </aside>
      <div className="lg:pl-64">
        {isViewingAs && (
          <div className="sticky top-0 z-40 h-10 -ml-0 lg:-ml-64 flex items-center justify-between gap-3 px-4 sm:px-6 bg-amber-500 text-slate-950 text-xs sm:text-sm font-medium" data-testid="view-as-banner">
            <span className="flex items-center gap-2 truncate"><Eye size={15} />Stai vedendo il portale come <strong className="truncate" data-testid="view-as-company">{companyName}</strong></span>
            <button onClick={exitViewAs} className="inline-flex items-center gap-1 rounded-sm bg-slate-950 text-amber-300 px-2.5 py-1 hover:bg-slate-900 transition-colors shrink-0" data-testid="exit-view-as-button">
              <X size={13} />Torna alla consolle
            </button>
          </div>
        )}
        <header className={`sticky z-30 h-16 flex items-center justify-between gap-4 px-4 sm:px-8 bg-[#0B0F17]/80 backdrop-blur-md border-b border-slate-800 ${isViewingAs ? "top-10" : "top-0"}`}>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-[#0F172A] border-slate-800">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Sidebar onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-emerald-400" /> Sessione protetta con doppia autenticazione
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right leading-tight">
              <div className="text-sm font-medium" data-testid="header-user-name">{user.name || user.email}</div>
              <div className="text-xs text-slate-500" data-testid="header-user-role">{subtitle}</div>
            </div>
            <Button variant="outline" size="sm" onClick={doLogout} className="border-slate-700 bg-transparent" data-testid="logout-button">
              <LogOut size={15} className="mr-1.5" />Esci
            </Button>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-[1600px]">{children}</main>
      </div>
    </div>
  );
}
